import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SelectField } from 'modules/Common/form/v2';
import { useTranslation } from 'react-i18next';
import InstallationRealEmissionsModal from 'modules/Installations/components/InstallationRealEmissionsModal';
import useEmissionStore from 'modules/Suppliers/stores/emissionStore';
import {
  correctSupplierFactor,
  publishSupplierFactor,
} from 'modules/FreeSupplier/supplierFactorApi';
import { useSupplierFactorActions } from 'modules/FreeSupplier/useSupplierFactors';
import { useCreateSupplierDraft } from 'modules/FreeSupplier/useSupplierDrafts';
import {
  emissionModalDataToCorrectBody,
  emissionModalDataToDraftCreateBody,
  emissionModalDataToPublishBody,
} from 'modules/FreeSupplier/supplierFactorPayload';
import InfoLabel from './InfoLabel';

// Free-supplier emissions add/edit. REUSES the real operator emissions form
// (InstallationRealEmissionsModal — V2 form, CnGoodField, ProductionMethodsTab,
// SEFA included) per D48, but re-points its writes at the VERSIONED free-supplier
// factor endpoints instead of /emissions/operator:
//   .post  → POST /supplier/drafts (S10 "Save as draft", the default for a new
//            entry) OR POST /supplier/factors/publish ("Publish now")
//   .patch → POST /supplier/factors/correct  (a successor revision, addressed by
//            the current revision id, NOT the EmissionsFactor _id — see D-2)
// The modal's submit data is mapped onto the publish/correct/draft contracts in
// supplierFactorPayload.js, where SEFA is enforced (D-1) and the modal's
// inclusive dates become the contract's half-open interval (D-3). On success the
// parent's handleRefresh invalidates the factor list so the Sync badge advances.
// Item 20: same label + info-popover idiom item 19 shipped on the upload
// page (CommsTemplateDropzone) — a request only names the product (CN
// code), never the installation, so the choice is always the supplier's to
// make explicitly, every time.
//
// These two, plus INSTALLATION_REQUIRED_ERROR and SAVE_MODE_OPTIONS below,
// used to be module-scope constants. They're only ever read inside this
// file's own components (never imported elsewhere), so each is now computed
// from the owning component's own `t` instead of being hoisted — see
// SupplierEmissionsModal's identityChangeNotice/targetInstallationHelp/
// installationRequiredError and SaveModeToggle's buildSaveModeOptions.

export const SupplierSaveMode = Object.freeze({ Draft: 'draft', Publish: 'publish' });

// Module-scope (not exported/reused elsewhere) — built as a function of `t`,
// called via useMemo inside SaveModeToggle below.
const buildSaveModeOptions = (t) => [
  {
    value: SupplierSaveMode.Draft,
    label: t('freeSupplier:supplierEmissionsModal.saveAsDraftLabel', 'Save as draft'),
    hint: t(
      'freeSupplier:supplierEmissionsModal.saveAsDraftHint',
      'Review and confirm later — nothing is shared with declarants yet.',
    ),
  },
  {
    value: SupplierSaveMode.Publish,
    label: t('freeSupplier:supplierEmissionsModal.publishNowLabel', 'Publish now'),
    hint: t(
      'freeSupplier:supplierEmissionsModal.publishNowHint',
      'Shares this factor with your connected declarants immediately.',
    ),
  },
];

// A NEW entry's save-mode choice (S10 draft-by-default), plus an optional
// "skip this line" action for a caller walking through several lines (the
// data-request "provide data" flow). Rendered above the reused form via
// InstallationRealEmissionsModal's `modeControl` slot — never shown for an
// edit of an already-published factor (correcting published data via a
// draft would be misleading, so that flow keeps the original publish/correct
// behavior unconditionally).
function SaveModeToggle({ mode, onChange, onSkip }) {
  const { t } = useTranslation('freeSupplier');
  const saveModeOptions = useMemo(() => buildSaveModeOptions(t), [t]);
  return (
    <div className="mb-4 p-3 rounded-xl border border-slate-200 bg-slate-50">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs font-semibold text-slate-700 m-0">
          {t('supplierEmissionsModal.howShouldThisBeSaved', 'How should this be saved?')}
        </p>
        {onSkip ? (
          <button
            type="button"
            onClick={onSkip}
            className="text-[11px] font-medium text-slate-400 hover:text-slate-600 underline underline-offset-2"
          >
            {t('supplierEmissionsModal.skipThisLine', 'Skip this line')}
          </button>
        ) : null}
      </div>
      <div
        role="radiogroup"
        aria-label={t('supplierEmissionsModal.saveModeAriaLabel', 'Save mode')}
        className="flex flex-col sm:flex-row gap-2"
      >
        {saveModeOptions.map((opt) => {
          const selected = mode === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(opt.value)}
              className={`flex-1 text-left rounded-xl border-2 px-3 py-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40 ${
                selected
                  ? 'border-primaryColor bg-primaryColor/5'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <span className="block text-xs font-semibold text-slate-800">{opt.label}</span>
              <span className="block text-[11px] text-slate-500 mt-0.5">{opt.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function SupplierEmissionsModal({
  show,
  onClose,
  operator,
  installation,
  selectedEmission,
  handleRefresh,
  // Q3 (S7 fix batch 3): an optional caller-supplied notice for the shared
  // modal's existing CgDialog `description` slot — e.g.
  // RequestDirectEntryPage surfacing "Requested: 7208 — pick the specific
  // good you produce" when a request line's CN code is a chapter/heading
  // level code it couldn't safely auto-select. Takes precedence over the
  // identity-change notice below (the two are mutually exclusive in
  // practice: a request-tailored direct entry is always a NEW publish, so
  // `currentRevisionId` — and therefore identityChangeNotice — is unset).
  contextNotice,
  // S10 draft-by-default: the save-mode choice a NEW entry opens with.
  // EmissionFactorsPage's plain "Add emissions data" leaves this at the
  // default (draft — nothing publishes until the supplier confirms it from
  // Review drafts); the data-request "provide data" flow passes
  // SupplierSaveMode.Publish instead, because fulfilment is computed from
  // PUBLISHED factors only — a draft never satisfies a request. Ignored
  // once editing an already-published factor (that flow is always a
  // publish/correct, never a draft).
  defaultSaveMode = SupplierSaveMode.Draft,
  // Lets a caller keep the dialog open after a successful save instead of
  // closing it — the data-request "provide data" flow re-seeds this same
  // modal for the request's next line rather than closing between lines.
  closeOnSuccess = true,
  // Overrides the default per-mode success toast copy below (e.g. the
  // data-request flow's own "All N products provided" completion message).
  successMessage,
  // When set, renders a "Skip this line" action next to the save-mode
  // toggle (data-request flow only) — advances without saving.
  onSkip,
  // Item 20: an additive, opt-in "Target installation" picker rendered
  // above the form (via the modeControl slot) — `{ installations, required }`.
  // Only the data-request "provide data" flow passes this: a request line
  // names a product (CN code), never an installation, so the caller-supplied
  // `installation` prop is never trustworthy here and is ignored in favor of
  // this in-modal, ALWAYS-empty-on-open selection. Every other caller omits
  // this prop and keeps today's behavior (the `installation` prop as-is).
  installationChoice,
}) {
  const { t } = useTranslation('freeSupplier');
  const { setSelectedEmission, clearSelectedEmission } = useEmissionStore();
  const { invalidate } = useSupplierFactorActions();
  const createDraft = useCreateSupplierDraft();
  // Item 20: starts empty on every mount — no prefill, no heuristics. The
  // caller (DataRequestsPage) already remounts this component (via `key`)
  // for every queue line, so a fresh `useState('')` alone guarantees the
  // reset the spec asks for; nothing here carries a choice across lines.
  const [choiceInstallationId, setChoiceInstallationId] = useState('');
  const chosenInstallation = installationChoice
    ? (installationChoice.installations || []).find(
        (inst) => String(inst._id) === choiceInstallationId,
      ) || null
    : installation;
  const installationId = chosenInstallation?._id;
  const installationRequired = Boolean(installationChoice?.required);
  const missingRequiredInstallation = installationRequired && !chosenInstallation;
  // The revision id the edit flow must correct (plumbed from the list response's
  // currentRevision.id — the EmissionsFactor _id would be the wrong handle).
  const currentRevisionId = selectedEmission?.currentRevisionId;
  const isEditingPublished = Boolean(currentRevisionId);
  // D-4: the reused modal leaves identity (CN code/product/methods/period)
  // editable in correction mode too — surface the consequence via the shared
  // modal's existing CgDialog `description` slot rather than forking it.
  const identityChangeNotice = currentRevisionId
    ? t(
        'supplierEmissionsModal.identityChangeNotice',
        'Changing CN code, product, methods, or period publishes a new factor line — the previous line is preserved in history.',
      )
    : undefined;

  const [saveMode, setSaveMode] = useState(defaultSaveMode);

  // Sync the shared emission store only on open/close. selectedEmission is
  // captured once at open time so parent re-renders (e.g. react-query settling)
  // don't reset the store and wipe in-progress input — same guard as the
  // operator wrapper. The save-mode choice resets to the caller's default on
  // every fresh open too, so a stale "Publish now" pick from a previous line
  // never leaks into the next.
  const snapshot = useRef(null);
  useEffect(() => {
    if (show) {
      snapshot.current = selectedEmission;
      if (selectedEmission) setSelectedEmission(selectedEmission);
      else clearSelectedEmission();
      setSaveMode(defaultSaveMode);
    } else {
      clearSelectedEmission();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Shaped like an axios error response so it renders through the same
  // notifyError path (title/body from err.response.data) as every other
  // save failure here, instead of the generic "Something went wrong" fallback
  // notifyError shows for anything it doesn't recognize as that shape.
  const installationRequiredError = useMemo(
    () => ({
      response: {
        data: {
          resource: t('supplierEmissionsModal.targetInstallationLabel', 'Target installation'),
          action: t('supplierEmissionsModal.installationRequiredAction', 'is required'),
          message: t(
            'supplierEmissionsModal.installationRequiredMessage',
            'Choose which installation this value belongs to before saving.',
          ),
        },
      },
    }),
    [t],
  );

  // Route the modal's writes to the versioned supplier factor endpoints. Errors
  // (SEFA missing, in-use, backend validation) propagate to the modal's own
  // catch → notifyError, which keeps the form open with the entered values
  // intact (phase-4 mutation UX contract: await, no close-before-success).
  // The submit button is already `disabled` while a required installation is
  // unpicked (below) — this is a defense-in-depth backstop, not the primary
  // gate, in case a caller ever reaches submit some other way.
  const http = {
    post: (_url, body) =>
      missingRequiredInstallation
        ? Promise.reject(installationRequiredError)
        : !isEditingPublished && saveMode === SupplierSaveMode.Draft
          ? createDraft.mutateAsync(emissionModalDataToDraftCreateBody(body))
          : publishSupplierFactor(emissionModalDataToPublishBody(body)),
    patch: (_url, body) =>
      missingRequiredInstallation
        ? Promise.reject(installationRequiredError)
        : correctSupplierFactor(emissionModalDataToCorrectBody(body, currentRevisionId)),
  };

  const effectiveMode = isEditingPublished ? SupplierSaveMode.Publish : saveMode;

  // Item 2: a publish/correct here always changes the versioned supplier
  // factor list, and can change data-request fulfilment coverage — invalidate
  // both directly (the SAME convention useSupplierFactorActions().invalidate
  // already uses for withdraw/confirm) rather than relying on each caller's
  // own handleRefresh to know about them. A draft create already invalidated
  // the drafts list inside useCreateSupplierDraft — nothing published
  // changed, so the factor/inbox lists don't need it.
  const refreshAfterWrite = async () => {
    if (effectiveMode !== SupplierSaveMode.Draft) {
      await invalidate();
    }
    await handleRefresh?.({ mode: effectiveMode });
  };

  const createSuccessMessage =
    successMessage ??
    (!isEditingPublished && saveMode === SupplierSaveMode.Draft
      ? t(
          'supplierEmissionsModal.draftSavedMessage',
          'Draft saved — review and publish it from Review drafts',
        )
      : undefined);

  const submitLabel = !isEditingPublished
    ? saveMode === SupplierSaveMode.Draft
      ? t('supplierEmissionsModal.saveAsDraftLabel', 'Save as draft')
      : t('supplierEmissionsModal.publishNowLabel', 'Publish now')
    : undefined;

  const installationOptions = useMemo(
    () =>
      (installationChoice?.installations || []).map((inst) => ({
        value: String(inst._id),
        label: inst.name,
      })),
    [installationChoice],
  );

  // Item 20: rendered above the form via the modeControl slot — "Target
  // installation" + the same InfoLabel hover-popover idiom item 19 shipped
  // on the upload page. Always empty on open; the SelectField's own
  // placeholder ("Choose an installation") makes that visible rather than
  // guessing.
  const installationSelector = installationChoice ? (
    <div className="mb-4 max-w-xs">
      <InfoLabel
        htmlFor="provideDataInstallation"
        helpTitle={t('supplierEmissionsModal.targetInstallationLabel', 'Target installation')}
        help={t(
          'supplierEmissionsModal.targetInstallationHelp',
          'Choose which of your installations this value belongs to. The request only names the product (CN code), not the installation.',
        )}
        triggerTestId="targetInstallationHelp"
      >
        {t('supplierEmissionsModal.targetInstallationLabel', 'Target installation')}
        {installationRequired ? ' *' : ''}
      </InfoLabel>
      <SelectField
        id="provideDataInstallation"
        field={{
          name: 'targetInstallation',
          value: choiceInstallationId,
          onChange: setChoiceInstallationId,
          onBlur: () => {},
        }}
        options={installationOptions}
        placeholder={t(
          'supplierEmissionsModal.chooseInstallationPlaceholder',
          'Choose an installation',
        )}
      />
    </div>
  ) : null;

  const modeControl =
    installationSelector || !isEditingPublished ? (
      <>
        {installationSelector}
        {!isEditingPublished ? (
          <SaveModeToggle
            mode={saveMode}
            onChange={setSaveMode}
            onSkip={onSkip}
          />
        ) : null}
      </>
    ) : undefined;

  return (
    <InstallationRealEmissionsModal
      show={show}
      onClose={onClose}
      http={http}
      contextType="operator"
      operatorId={operator?._id}
      installationId={installationId}
      installation={chosenInstallation}
      description={contextNotice ?? identityChangeNotice}
      handleRefresh={refreshAfterWrite}
      modeControl={modeControl}
      submitLabel={submitLabel}
      submitDisabled={missingRequiredInstallation}
      createSuccessMessage={createSuccessMessage}
      closeOnSuccess={closeOnSuccess}
    />
  );
}
